"use client"

import { motion } from "framer-motion"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Image from "next/image"
import React, { useState, useContext, type JSX } from "react"

import {
  Asset,
  RichTextContent,
  StatsCollection,
  Video as VideoType,
} from "@/app/impact/_types/impact"
import { ContentfulRichText } from "@/helpers/ContentfulRichText"

import AppContext from "../../AppContext"
import Button from "../../Button"
import PageGrid from "../../PageGrid"
import VideoPlayer from "../../VideoPlayer"

import StatTout from "./StatTout"

type PillarProps = {
  open?: boolean
  title: string
  heading: string | JSX.Element
  description: RichTextContent
  primaryButton: string
  secondaryButton: string
  statsCollection: StatsCollection
  heroImage: Asset
  video?: VideoType | null
  detailsModal?: any
  logoModalCollection?: any
}

const Pillar: React.FC<PillarProps> = ({
  open: pillarOpen = false,
  title,
  heading,
  description,
  heroImage,
  video,
  primaryButton,
  secondaryButton,
  statsCollection,
  detailsModal,
  logoModalCollection,
}) => {
  const [open, setOpen] = useState(pillarOpen)
  const animations = {
    open: {
      height: "auto",
      opacity: 1,
      transition: {
        bounce: 0,
      },
    },
    closed: {
      height: 0,
      opacity: 0,
    },
  }

  const context = useContext(AppContext)

  const renderMedia = () => {
    if (video) {
      return (
        <div className="mt-4 col-span-full md:col-span-5">
          <VideoPlayer
            videoURL={video.videoLink}
            wistiaVideo={video.wistiaVideo}
            title={video.videoName}
            image={video.videoCover?.url}
            className="rounded-lg"
          />
        </div>
      )
    }

    return (
      <Image
        alt=""
        src={heroImage?.url}
        className="mt-4 rounded-lg col-span-full md:col-span-5 object-contain"
        loading="eager"
        width={heroImage?.width || 452}
        height={heroImage?.height || 283}
        objectPosition="top"
      />
    )
  }

  return (
    <div className="pillar">
      <div className="pillar-button w-full">
        <button
          onClick={() => {
            setOpen(!open)
          }}
          type="button"
          className="flex justify-between w-full px-4 py-4 mx-auto md:px-8 lg:px-4 xl:max-w-layout-xl lg:max-w-layout-lg lg:py-6 body3 md:body2 lg:body1"
        >
          <span>{title}</span>
          <span>{open ? "-" : "+"}</span>
        </button>
      </div>
      <motion.div
        variants={animations}
        initial={false}
        animate={open ? "open" : "closed"}
        className="pillar-content h-0 overflow-hidden opacity-0"
        onAnimationComplete={() => {
          ScrollTrigger.refresh()
        }}
      >
        <PageGrid className="py-8">
          <div className="p-4 col-span-full lg:col-span-7 body-3 rich-text-content impact-report-post-content">
            <h2 className="mb-2 heading1 md:headline5 lg:headline3 text-gray-90">
              {heading}
            </h2>
            <ContentfulRichText document={description.json as any} />
          </div>
          {renderMedia()}
          <div className="flex flex-col mt-4 mb-8 md:block col-span-full lg:mt-6 lg:px-4 lg:mb-12">
            <Button
              theme="light"
              variant="secondary"
              onClick={(event) => {
                context.setImpactReportSpotlightModal(true, { detailsModal })
                event.preventDefault()
              }}
              className="w-fit"
            >
              {primaryButton}
            </Button>
            <Button
              theme="light"
              variant="tertiary"
              onClick={(event) => {
                context.setImpactReportPartnersModal(true, {
                  logoModalCollection,
                })
                event.preventDefault()
              }}
              className="mt-4 md:pl-4 md:mt-0 w-fit"
            >
              {secondaryButton}
            </Button>
          </div>
          {/* Stats call out goes here */}
          {statsCollection.items.map((tout) => (
            <StatTout
              key={tout.title}
              title={tout.title}
              description={tout.description}
              button={tout.button}
            />
          ))}
        </PageGrid>
      </motion.div>
    </div>
  )
}

export default Pillar
